import * as React from "react";
import * as qs from 'query-string';
import { ICenter, IBaseResponse, ICenterBase } from "@nkr/models/index";
import { IContactListResponse, IApplicationState, AppActionCreators, ContactActionCreators, IContactState, ICenterListResponse, CenterActionCreators, IContactPositionListResponse, IContactLookupDataResponse, IBasicCenterListResponse, IAppState } from "@nkr/store/index";
import { Dispatch, connect } from "react-redux";
import AdminList from "./admin/list";
import CenterList from "./center/list";
import { toast } from "react-toastify";
import { downloadFile } from '../../../services/download-file';
import { IFilters } from './filters';

interface IProps {
    centerId?: number,
    isCenter: boolean
}

interface IStateProps {
    contact: IContactState,
    centerList: ICenter[],
    app: IAppState,
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getContactList: (organType: number, centerId?: number, type?: number, search?: string, roles?: { entityType: number, roleId: number }[], includeDeleted?: boolean) => Promise<IContactListResponse>,
    getBasicCenterList: () => Promise<IBasicCenterListResponse>,
    getLookupData: (centerId: number, organType: number) => Promise<IContactLookupDataResponse>,
    deleteContact: (contactId: number) => Promise<IBaseResponse>,
    hardDeleteContact: (contactId: number) => Promise<IBaseResponse>,
    hardDeleteContactsBulk: (contactIds: number[]) => Promise<IBaseResponse>,
    getContactsResults: (centerId: number, contactEntityTypeId: number, roles: string, search: string) => Promise<any>,
    exportContacts: (centerId: number, contactEntityType: number, adminRoles: string[], centerRole: number, search: string) => Promise<any>
}

type TProps = IStateProps & IDispatchProps & IProps

interface IState {
    centerList: ICenterBase[],
    filters: IFilters,
    showDeleted: boolean
}

class Contacts extends React.Component<TProps, IState> {
    readonly state: IState = {
        centerList: [],
        filters: {
            centerId: 0,
            contactEntityTypeId: 0,
            search: '',
            centerRole: 0,
            adminRoles: []
        },
        showDeleted: false
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        let parsed = qs.parse(location.search);
        let centerId = parsed.centerId != null ? parseInt(parsed.centerId) : 0;
        let filters = { ...this.state.filters, centerId };

        if (this.props.centerId != null) {
            filters.centerId = this.props.centerId;
        }

        let roles = filters.adminRoles.map(x => {
            return {
                entityType: x.extra.entityType,
                roleId: x.extra.positionId
            };
        });

        this.props.showLoading("Getting contacts, one moment please...").then(() => {
            Promise.all([
                this.props.getContactList(this.props.app.site.organType, this.props.centerId, filters.contactEntityTypeId, filters.search, roles, this.state.showDeleted),
                this.props.getBasicCenterList(),
                this.props.getLookupData(this.props.centerId, this.props.app.site.organType)
            ])
                .then(([contactListResponse, centerListResponse, lookupDataResponse]) => {
                    this.setState({
                        centerList: centerListResponse.data.results,
                        filters
                    });
                })
                .catch(error => {
                    // Handle error silently
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    refreshData = () => {
        let { centerId, contactEntityTypeId, search, adminRoles } = this.state.filters;

        let roles = adminRoles.map(x => {
            return {
                entityType: x.extra.entityType,
                roleId: x.extra.positionId
            };
        });

        this.props.getContactList(this.props.app.site.organType, centerId, contactEntityTypeId, search, roles, this.state.showDeleted);
    }

    setUrlParameters = (filters: IFilters) => {
        let paramList = [];

        // paramList.push(encodeURI('lt=' + tab));
        paramList.push(encodeURI('center=' + filters.centerId));
        paramList.push(encodeURI('et=' + filters.contactEntityTypeId));

        let params = paramList.join('&');

        let newUrl = '/admin/serology-tracking?' + params

        window.history.pushState({}, '', newUrl);
    };

    handleSearch = () => {
        this.props.showLoading('Getting contacts, one moment please...').then(() => {
            this.refreshData();

            this.props.hideLoading();
        });
    }

    handleFilterChange = (filters: IFilters) => {
        this.setState({
            filters
        });
    }

    handleShowDeletedChange = (showDeleted: boolean) => {
        this.setState({
            showDeleted
        }, () => {
            this.getData();
        });
    }

    handleShowDeletedChangeWithPermission = (showDeleted: boolean) => {
        if (showDeleted && !this.props.app.user?.canHardDelete) {
            this.props.showLoading("Checking permissions...").then(() => {
                alert("You do not have permission to view deleted contacts.");
                this.props.hideLoading();
            });

            return;
        }

        this.handleShowDeletedChange(showDeleted);
    }

    handleGetContactsResults = () => {
        let { centerId, contactEntityTypeId, adminRoles, search } = this.state.filters;

        this.props.getContactsResults(centerId, contactEntityTypeId, adminRoles.join(), search).then((response: any) => {
            downloadFile(response.data, `AllNKRContacts.csv`, 'text/csv');
        });
    }

    handleDeleteContact = (contactId: number) => {
        this.props.showLoading("Deleting contact, one moment please...").then(() => {
            this.props.deleteContact(contactId)
                .then(response => {
                    this.refreshData();

                    if (response.data.success) {
                        toast.success('Contact deleted successfully');
                    }
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    handleHardDeleteContact = (contactId: number) => {
        if (!this.props.app.user?.canHardDelete) {
            toast.error("You do not have permission to hard delete contacts.");
            return;
        }

        this.props.showLoading("Permanently deleting contact, one moment please...").then(() => {
            this.props.hardDeleteContact(contactId)
                .then(response => {
                    this.refreshData();

                    if (response.data.success) {
                        toast.success('Contact permanently deleted');
                    }
                })
                .catch(error => {
                    toast.error('Failed to delete contact. Please try again.');
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    handleHardDeleteContactsBulk = (contactIds: number[]) => {
        if (!this.props.app.user?.canHardDelete) {
            toast.error("You do not have permission to hard delete contacts.");
            return;
        }

        this.props.showLoading(`Permanently deleting ${contactIds.length} contacts, one moment please...`).then(() => {
            this.props.hardDeleteContactsBulk(contactIds)
                .then(response => {
                    this.refreshData();

                    if (response.data.success) {
                        toast.success(`Successfully permanently deleted ${contactIds.length} contacts`);
                    }
                })
                .catch(error => {
                    toast.error('Failed to delete contacts. Please try again.');
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    handleExport = () => {
        let { centerId, contactEntityTypeId, adminRoles, centerRole, search } = this.state.filters;

        let adminRoleList = adminRoles.map(item => {
            return `${item.extra.entityType}|${item.extra.positionId}`;
        });

        this.props.showLoading('One moment please...').then(() => {
            this.props.exportContacts(centerId, contactEntityTypeId, adminRoleList, centerRole, search)
                .then(response => {
                    downloadFile(response.data, 'contacts.csv', 'text/csv');
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    render = () => {
        return (
            <>
                {this.props.isCenter == false && (
                    <AdminList
                        data={this.props.contact.list}
                        centers={this.state.centerList}
                        contactEntityTypes={this.props.contact.lookupData.contactEntityTypes}
                        roles={this.props.contact.lookupData.positions}
                        types={this.props.contact.lookupData.contactEntityTypes}
                        filters={this.state.filters}
                        showDeleted={this.state.showDeleted}
                        currentUser={this.props.app.user}
                        onSearch={this.handleSearch}
                        onFilterChange={this.handleFilterChange}
                        onShowDeletedChange={this.handleShowDeletedChangeWithPermission}
                        onDelete={this.handleDeleteContact}
                        onHardDelete={this.handleHardDeleteContact}
                        onHardDeleteBulk={this.handleHardDeleteContactsBulk}
                        onOutlookExport={this.handleGetContactsResults}
                        onExport={this.handleExport}
                    />
                )}

                {this.props.isCenter == true && (
                    <CenterList
                        data={this.props.contact.list}
                        centers={this.state.centerList}
                        contactEntityTypes={this.props.contact.lookupData.contactEntityTypes}
                        entityTypePositions={this.props.contact.lookupData.positions}
                        types={this.props.contact.lookupData.contactEntityTypes}
                        centerId={this.props.centerId}
                        filters={this.state.filters}
                        showDeleted={this.state.showDeleted}
                        currentUser={this.props.app.user}
                        organType={this.props.app.site.organType}
                        leadCoordinatorId={this.props.app.center?.leadCoordinatorId}
                        onFilterChange={this.handleFilterChange}
                        onShowDeletedChange={this.handleShowDeletedChangeWithPermission}
                        onDelete={this.handleDeleteContact}
                        onHardDelete={this.handleHardDeleteContact}
                        onExport={this.handleExport}
                    />
                )}
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        contact: appState.contact,
        centerList: appState.centers.list,
        app: appState.app,
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getContactList: (organType, centerId, type, search, roles, includeDeleted) => {
            return dispatch(ContactActionCreators.getContactList(organType, centerId, type, search, roles, includeDeleted));
        },
        getBasicCenterList: () => {
            return dispatch(CenterActionCreators.getBasicCenterList(false));
        },
        getLookupData: (centerId, organType) => {
            return dispatch(ContactActionCreators.getContactLookupData(centerId, organType));
        },
        deleteContact: (contactId) => {
            return dispatch(ContactActionCreators.deleteContact(contactId));
        },
        hardDeleteContact: (contactId) => {
            return dispatch(ContactActionCreators.hardDeleteContact(contactId));
        },
        hardDeleteContactsBulk: (contactIds) => {
            return dispatch(ContactActionCreators.hardDeleteContactsBulk(contactIds));
        },
        getContactsResults: (centerId: number, contactEntityTypeId: number, roles: string, search: string) => {
            return dispatch(ContactActionCreators.getContactResults(centerId, contactEntityTypeId, roles, search));
        },
        exportContacts: (centerId, contactEntityType, adminRoles, centerRole, search) => {
            return dispatch(ContactActionCreators.exportContacts(centerId, contactEntityType, adminRoles, centerRole, search));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps, mapDispatchToProps)(Contacts)